import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { Box, Button, Flex, Text } from 'core/components';
import { Field, Select } from 'core/form';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { AppProtocolsDictionary } from '@kentik/ui-shared/dictionary/types';
import { SortableMenu } from 'app/views/boards/panels/ude/SortableMenu';
import { Position } from '@blueprintjs/core';
import DimensionMenu from 'app/views/boards/panels/ude/components/DimensionMenu';
import DropdownMenu from 'core/components/DropdownMenu';
import { getGroupedDimensions } from 'app/stores/ude/measurementTransforms';
import { MeasurementHash } from 'app/stores/dictionary/useDictionaryMeasurements';
import { usePopoverGate } from './PopoverGateContext';
import { useUdeForm } from './useUdeForm';

interface UdePanelDimensionsProps {
  measurements: Measurement[];
  measurementHash: MeasurementHash;
  appProtocols?: AppProtocolsDictionary;
  headerVariant?: 'default' | 'pane';
}

const UdePanelDimensions = observer(
  ({ measurements, measurementHash, appProtocols, headerVariant }: UdePanelDimensionsProps) => {
    const { isOpen: isDropdownOpen, setOpen: setDropdownOpen } = usePopoverGate('dimensions');

    const form = useUdeForm();
    const measurement = form.getValue('config.data.measurement');
    const metrics = form.getValue('config.data.metrics') || [];
    const dimensions = form.getValue('config.data.dimensions') || [];
    const hasMeasurement = !!measurement;

    const handleRemoveDimension = useCallback(
      (dimensionName: string) => {
        const currentDimensions = form.getValue('config.data.dimensions') || [];
        const updatedDimensions = currentDimensions.filter((dimension) => dimension.name !== dimensionName);
        form.setValue('config.data.dimensions', updatedDimensions);
      },
      [form]
    );

    const handleAddDimension = useCallback(
      (newDim: string | { key: string }) => {
        const currentDimensions = form.getValue('config.data.dimensions') || [];
        const newDimName = typeof newDim === 'string' ? newDim : newDim.key;

        const existingIndex = currentDimensions.findIndex((dimension) => dimension.name === newDimName);
        if (existingIndex >= 0) {
          form.setValue(
            'config.data.dimensions',
            currentDimensions.filter((_, i) => i !== existingIndex)
          );
        } else {
          form.setValue('config.data.dimensions', [...currentDimensions, { name: newDimName }]);
        }
      },
      [form]
    );

    const handleToggleBothDirections = useCallback(
      (keys: string[]) => {
        const currentDimensions = form.getValue('config.data.dimensions') || [];
        const currentNames = new Set(currentDimensions.map((d: { name: string }) => d.name));
        const allSelected = keys.every((key) => currentNames.has(key));

        if (allSelected) {
          const keySet = new Set(keys);
          form.setValue(
            'config.data.dimensions',
            currentDimensions.filter((d: { name: string }) => !keySet.has(d.name))
          );
        } else {
          const newDimensions = [...currentDimensions];
          keys.forEach((key) => {
            if (!currentNames.has(key)) {
              newDimensions.push({ name: key });
            }
          });
          form.setValue('config.data.dimensions', newDimensions);
        }
      },
      [form]
    );

    const handleReorder = useCallback(
      ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        const currentDimensions = form.getValue('config.data.dimensions') || [];
        const updatedDimensions = Array.from(currentDimensions);
        const [movedDimension] = updatedDimensions.splice(oldIndex, 1);
        updatedDimensions.splice(newIndex, 0, movedDimension);
        form.setValue('config.data.dimensions', updatedDimensions);
      },
      [form]
    );

    const disabledDimensions = new Set(metrics.map((metric) => metric.name));

    const content = (
      <Flex flexDirection="column" px="12px" pt={0} pb={0} display="flex" gap={1}>
        {!dimensions?.length && (
          <Text muted small fontWeight="normal">
            No dimensions selected
          </Text>
        )}
        {dimensions?.length > 0 && (
          <SortableMenu
            measurement={form.getValue('config.data.measurement') || ''}
            type="dimension"
            handleRemoveItem={handleRemoveDimension}
            measurements={measurements}
            items={dimensions}
            onSortEnd={handleReorder}
          />
        )}
        {measurement?.startsWith('/metrics/') && (
          <Field name="config.data.reAgg.fn" mt={2} mb={0}>
            <Select />
          </Field>
        )}
      </Flex>
    );

    const groupedDimensions = getGroupedDimensions(measurement ?? '', measurementHash)
      .map((group) => ({ ...group, items: group.items.filter((item) => !item.nodeData?.FilterOnly) }))
      .filter((group) => group.items.length > 0);

    const addButton = hasMeasurement ? (
      <Box ml="auto">
        <DropdownMenu
          isOpen={isDropdownOpen}
          onInteraction={(nextOpenState, e) => {
            if (nextOpenState) {
              setDropdownOpen(true);
              return;
            }
            const target = (e?.target as HTMLElement | null) ?? null;
            const isInsideContent = !!target?.closest('.ude-dimension-selector-popover');
            const isEscape = (e as unknown as KeyboardEvent | undefined)?.key === 'Escape';
            if (isInsideContent && !isEscape) {
              return;
            }
            setDropdownOpen(false);
          }}
          position={Position.RIGHT_TOP}
          popoverClassName="ude-dimension-selector-popover"
          boundary="viewport"
          modifiers={{
            flip: { enabled: true },
            hide: { enabled: false },
            preventOverflow: { enabled: true, padding: 20 }
          }}
          usePortal
          canEscapeKeyClose
          content={
            <DimensionMenu
              measurement={measurement}
              measurementHash={measurementHash}
              groupedDimensions={groupedDimensions}
              selectedDimensions={dimensions}
              disabledDimensions={disabledDimensions}
              appProtocols={appProtocols}
              onSelect={handleAddDimension}
              onSelectBoth={handleToggleBothDirections}
            />
          }
        >
          <Button
            small
            minimal
            intent="primary"
            text="Add"
            onClick={() => setDropdownOpen(true)}
            active={isDropdownOpen}
          />
        </DropdownMenu>
      </Box>
    ) : null;

    return headerVariant === 'pane' ? (
      <>
        <Flex alignItems="center" gap={1} p="6px 8px 6px 16px">
          <Text small fontWeight="bold" style={{ textTransform: 'uppercase' as const }}>
            Dimensions
          </Text>
          {addButton}
        </Flex>
        {content}
      </>
    ) : (
      <Flex flexDirection="column" pb="12px">
        <Flex alignItems="center" gap={1} p="6px 8px 6px 16px">
          <Text small fontWeight="bold" style={{ textTransform: 'uppercase' as const }}>
            Dimensions
          </Text>
          {addButton}
        </Flex>
        {content}
      </Flex>
    );
  }
);

export default UdePanelDimensions;
